import React from 'react';
import { SimulationConfig, BallPlacementConfig } from './types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';

interface BallPlacementSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const BallPlacementSection: React.FC<BallPlacementSectionProps> = ({ config, setConfig }) => {
  const updateIndividualConfig = (index: number, updates: Partial<BallPlacementConfig>) => {
    setConfig(p => {
      const newConfigs = [...(p.ballPlacementConfigs || [])];
      if (!newConfigs[index]) {
        newConfigs[index] = {
          id: Math.random().toString(),
          mode: 'CENTER',
          posX: 0,
          posY: 0,
          border: 'TOP',
          placementAngle: 0,
          distance: 200,
          directionAngle: 0,
          speed: p.ballPlacementInitialSpeed || 10,
          randomDirection: p.ballPlacementRandomDirection || false
        };
      }
      newConfigs[index] = { ...newConfigs[index], ...updates };
      return { ...p, ballPlacementConfigs: newConfigs };
    });
  };

  return (
    <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border bg-blue-500/5 border-blue-500/10 mb-4">
      <div className="flex flex-col gap-3 p-3 rounded-2xl bg-black/5 border border-white/5">
        <SmartInput 
          label="Number of Balls" 
          min={1} 
          max={config.mode === 'ESCAPE' ? 10 : 100} 
          value={config.initialBalls} 
          onChange={v => setConfig(p => ({ ...p, initialBalls: v }))} 
          darkMode={config.darkMode} 
          settingKey="initialBalls"
        />
        <div className="text-[8px] opacity-40 -mt-2 text-center">Sets the number of balls for all modes (Escape limited to 10 for performance)</div>
      </div>

      <ControlGroup label="Placement Mode" settingKey="ballPlacementMode" darkMode={config.darkMode}>
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-black/5 border border-white/5">
          {(['CENTER', 'MANUAL', 'BORDER', 'ANGLE'] as const).map(m => (
            <button
              key={m}
              onClick={() => setConfig(p => ({ ...p, ballPlacementMode: m }))}
              className={`py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${
                config.ballPlacementMode === m 
                ? (config.darkMode ? 'bg-white text-black' : 'bg-black text-white') 
                : 'opacity-40 hover:opacity-100'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </ControlGroup>

      {config.ballPlacementMode === 'MANUAL' && (
        <div className="flex flex-col gap-2">
          <SmartInput label="Pos X (%)" min={-100} max={100} value={config.ballPlacementPosX} onChange={v => setConfig(p => ({ ...p, ballPlacementPosX: v }))} darkMode={config.darkMode} settingKey="ballPlacementPosX" />
          <SmartInput label="Pos Y (%)" min={-100} max={100} value={config.ballPlacementPosY} onChange={v => setConfig(p => ({ ...p, ballPlacementPosY: v }))} darkMode={config.darkMode} settingKey="ballPlacementPosY" />
        </div>
      )}

      {config.ballPlacementMode === 'BORDER' && (
        <ControlGroup label="Border Side" settingKey="ballPlacementBorder" darkMode={config.darkMode}>
          <div className="grid grid-cols-4 gap-1 p-1 rounded-xl bg-black/5 border border-white/5">
            {(['TOP', 'BOTTOM', 'LEFT', 'RIGHT'] as const).map(b => (
              <button
                key={b}
                onClick={() => setConfig(p => ({ ...p, ballPlacementBorder: b }))}
                className={`py-1.5 rounded-lg text-[8px] font-black uppercase transition-all ${
                  config.ballPlacementBorder === b 
                  ? (config.darkMode ? 'bg-white text-black' : 'bg-black text-white') 
                  : 'opacity-40 hover:opacity-100'
                }`}
              >
                {b[0]}
              </button>
            ))}
          </div>
        </ControlGroup>
      )}

      {config.ballPlacementMode === 'ANGLE' && (
        <div className="flex flex-col gap-2">
          <SmartInput label="Angle (°)" min={0} max={360} value={config.ballPlacementAngle} onChange={v => setConfig(p => ({ ...p, ballPlacementAngle: v }))} darkMode={config.darkMode} settingKey="ballPlacementAngle" />
          <SmartInput label="Dist (%)" min={0} max={100} value={config.ballPlacementDistance} onChange={v => setConfig(p => ({ ...p, ballPlacementDistance: v }))} darkMode={config.darkMode} settingKey="ballPlacementDistance" />
        </div>
      )}

      <div className="h-px bg-white/5 my-1" />

      <div className="flex flex-col gap-2">
        <SmartInput label="Initial Speed" min={0} max={100} value={config.ballPlacementInitialSpeed} onChange={v => setConfig(p => ({ ...p, ballPlacementInitialSpeed: v }))} darkMode={config.darkMode} settingKey="ballPlacementInitialSpeed" />
        <SmartInput label="Direction (°)" min={0} max={360} value={config.ballPlacementDirectionAngle} onChange={v => setConfig(p => ({ ...p, ballPlacementDirectionAngle: v }))} darkMode={config.darkMode} settingKey="ballPlacementDirectionAngle" />
      </div>

      <ControlGroup label="Random Direction" settingKey="ballPlacementRandomDirection" darkMode={config.darkMode}>
        <Toggle active={config.ballPlacementRandomDirection} onClick={() => setConfig(p => ({ ...p, ballPlacementRandomDirection: !p.ballPlacementRandomDirection }))} />
      </ControlGroup>

      <div className="h-px bg-white/5 my-1" />

      <ControlGroup label="Individual Configs" settingKey="ballPlacementIndividualEnabled" darkMode={config.darkMode}>
        <Toggle active={config.ballPlacementIndividualEnabled} onClick={() => setConfig(p => ({ ...p, ballPlacementIndividualEnabled: !p.ballPlacementIndividualEnabled }))} />
      </ControlGroup>

      {config.ballPlacementIndividualEnabled && (
        <div className="flex flex-col gap-3 mt-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
          {Array.from({ length: config.initialBalls || 1 }).map((_, i) => {
            const ballCfg = config.ballPlacementConfigs?.[i] || {
              posX: 0,
              posY: 0,
              directionAngle: 0,
              speed: config.ballPlacementInitialSpeed || 10,
              randomDirection: config.ballPlacementRandomDirection || false
            };
            const presets = [
              { name: 'Center', x: 0, y: 0 },
              { name: 'Top', x: 0, y: -90 },
              { name: 'Bottom', x: 0, y: 90 },
              { name: 'Left', x: -90, y: 0 },
              { name: 'Right', x: 90, y: 0 },
              { name: 'U-Mid', x: 0, y: -45 },
              { name: 'L-Mid', x: 0, y: 45 },
              { name: 'T-Left', x: -70, y: -70 },
              { name: 'T-Right', x: 70, y: -70 },
              { name: 'B-Left', x: -70, y: 70 },
              { name: 'B-Right', x: 70, y: 70 },
              { name: 'Edge', x: 95, y: 0 },
            ];

            return (
              <div key={i} className="p-3 rounded-2xl bg-black/10 border border-white/5 flex flex-col gap-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[9px] font-black uppercase text-blue-400">Ball {i + 1}</span>
                </div>
                
                <div className="flex flex-col gap-1">
                  <span className="text-[8px] font-black uppercase opacity-40">Presets</span>
                  <div className="grid grid-cols-4 gap-1">
                    {presets.map(p => (
                      <button
                        key={p.name}
                        onClick={() => updateIndividualConfig(i, { posX: p.x, posY: p.y })}
                        className={`py-1 rounded-md text-[7px] font-black uppercase border border-white/5 bg-white/5 hover:bg-white/10 transition-all`}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <SmartInput label="X (%)" min={-100} max={100} value={ballCfg.posX} onChange={v => updateIndividualConfig(i, { posX: v })} darkMode={config.darkMode} />
                  <SmartInput label="Y (%)" min={-100} max={100} value={ballCfg.posY} onChange={v => updateIndividualConfig(i, { posY: v })} darkMode={config.darkMode} />
                </div>
                <div className="flex flex-col gap-2">
                  <SmartInput label="Speed" min={0} max={100} value={ballCfg.speed} onChange={v => updateIndividualConfig(i, { speed: v })} darkMode={config.darkMode} />
                  <SmartInput label="Dir (°)" min={0} max={360} value={ballCfg.directionAngle} onChange={v => updateIndividualConfig(i, { directionAngle: v })} darkMode={config.darkMode} />
                </div>
                <div className="flex items-center justify-between p-1">
                  <span className="text-[8px] font-black uppercase opacity-40">Random Dir</span>
                  <Toggle active={ballCfg.randomDirection} onClick={() => updateIndividualConfig(i, { randomDirection: !ballCfg.randomDirection })} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
